import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCartDropdown, setShowCartDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const { user, logout } = useAuth();
  const { cartItems, removeFromCart, updateQuantity, getTotal, getTotalItems } = useCart();
  const navigate = useNavigate();
  const cartDropdownRef = useRef(null);
  const userDropdownRef = useRef(null);
  const searchRef = useRef(null);

  // Gérer les clics en dehors des dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cartDropdownRef.current && !cartDropdownRef.current.contains(event.target)) {
        setShowCartDropdown(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearch(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Gérer le survol du panier
  useEffect(() => {
    const handleMouseEnter = () => setShowCartDropdown(true);
    const handleMouseLeave = () => setShowCartDropdown(false);
    
    const cartElement = cartDropdownRef.current;
    if (cartElement) {
      cartElement.addEventListener('mouseenter', handleMouseEnter);
      cartElement.addEventListener('mouseleave', handleMouseLeave);
    }
    
    return () => {
      if (cartElement) {
        cartElement.removeEventListener('mouseenter', handleMouseEnter);
        cartElement.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, []);

  // Gérer le survol du menu utilisateur (hover + click)
  const handleUserMouseEnter = () => {
    setShowUserDropdown(true);
  };

  const handleUserMouseLeave = () => {
    setTimeout(() => {
      const isHovering = userDropdownRef.current?.matches(':hover');
      if (!isHovering && !showUserDropdown) {
        setShowUserDropdown(false);
      }
    }, 200);
  };

  const toggleUserDropdown = (e) => {
    e.stopPropagation();
    setShowUserDropdown(!showUserDropdown);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${searchQuery}`);
      setShowSearch(false);
      setSearchQuery('');
    }
  };

  const Icons = {
    Cart: () => (
      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-1.5 6M17 13l1.5 6M9 21h6M12 18v3" />
      </svg>
    ),
    Trash: () => (
      <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
      </svg>
    ),
    User: () => (
      <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    Search: () => (
      <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    ),
    Menu: () => (
      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    ),
    Minus: () => (
      <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
      </svg>
    ),
    Plus: () => (
      <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    )
  };

  return (
    <nav className="bg-white text-primary sticky top-0 z-50 shadow-md">
      <div className="container-custom">
        <div className="flex justify-between items-center h-20">
          {/* Logo - inchangé */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center">
              <span className="text-primary text-xl font-bold">A</span>
            </div>
            <span className="font-playfair text-2xl font-bold text-primary">
              Aqua<span className="text-secondary">HomeDelivery</span>
            </span>
          </Link>

          {/* Liens de navigation - couleur adaptée */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link to="/" className="text-primary hover:text-secondary transition font-medium">Accueil</Link>
            <Link to="/how-it-works" className="text-primary hover:text-secondary transition font-medium">Livraison</Link>
            <Link to="/products" className="text-primary hover:text-secondary transition font-medium">Boutique</Link>
            <Link to="/faq" className="text-primary hover:text-secondary transition font-medium">FAQ</Link>
            <Link to="/contact" className="text-primary hover:text-secondary transition font-medium">Contact</Link>
          </div>

          <div className="flex items-center space-x-5">
            {/* Barre de recherche */}
            <div className="relative" ref={searchRef}>
              <button onClick={() => setShowSearch(!showSearch)} className="hover:text-secondary transition">
                <Icons.Search />
              </button>
              {showSearch && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                  <form onSubmit={handleSearch} className="flex">
                    <input
                      type="text"
                      placeholder="Rechercher un produit..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="flex-1 px-4 py-2 text-darkGray outline-none"
                      autoFocus
                    />
                    <button type="submit" className="bg-primary text-white px-4 py-2">
                      <Icons.Search />
                    </button>
                  </form>
                </div>
              )}
            </div>

            {/* Panier */}
            <div className="relative" ref={cartDropdownRef}>
              <button 
                className="relative hover:text-secondary transition"
                onMouseEnter={() => setShowCartDropdown(true)}
              >
                <Icons.Cart />
                {getTotalItems() > 0 && (
                  <span className="absolute -top-2 -right-2 bg-secondary text-primary text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {getTotalItems()}
                  </span>
                )}
              </button>

              {showCartDropdown && (
                <div 
                  className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl overflow-hidden z-50 border border-gray-200"
                  onMouseEnter={() => setShowCartDropdown(true)}
                  onMouseLeave={() => setShowCartDropdown(false)}
                >
                  <div className="p-4 border-b bg-gray-50">
                    <h3 className="font-bold text-darkGray">Mon panier</h3>
                  </div>
                  
                  {cartItems.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <Icons.Cart />
                      <p className="mt-2">Votre panier est vide</p>
                    </div>
                  ) : (
                    <>
                      <div className="max-h-96 overflow-y-auto">
                        {cartItems.map(item => {
                          const price = typeof item.price === 'string' ? parseFloat(item.price) : item.price;
                          const total = price * item.quantity;
                          
                          return (
                            <div key={item.id} className="flex items-center gap-3 p-4 border-b hover:bg-gray-50">
                              <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden">
                                <img 
                                  src={item.image || '/default-product.png'} 
                                  alt={item.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-darkGray text-sm line-clamp-2">{item.name}</p>
                                <p className="text-secondary text-sm font-bold mt-1">{price.toFixed(2)} €</p>
                                <div className="flex items-center gap-2 mt-2">
                                  <button 
                                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                    className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300"
                                  >
                                    <Icons.Minus />
                                  </button>
                                  <span className="text-xs text-gray-600 w-8 text-center">{item.quantity}</span>
                                  <button 
                                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                    className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300"
                                  >
                                    <Icons.Plus />
                                  </button>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-darkGray">{total.toFixed(2)} €</p>
                                <button 
                                  onClick={() => removeFromCart(item.id)}
                                  className="text-red-400 hover:text-red-600 mt-1"
                                >
                                  <Icons.Trash />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="p-4 border-t bg-gray-50">
                        <div className="flex justify-between mb-3">
                          <span className="font-semibold text-darkGray">Total</span>
                          <span className="font-bold text-primary text-lg">{getTotal().toFixed(2)} €</span>
                        </div>
                        <div className="flex gap-3">
                          {user ? (
                            <>
                              <Link to="/cart" className="flex-1 bg-gray-200 text-darkGray text-center py-2 rounded-lg hover:bg-gray-300 transition text-sm font-semibold">
                                Voir le panier
                              </Link>
                              <Link to="/checkout" className="flex-1 bg-primary text-white text-center py-2 rounded-lg hover:bg-opacity-90 transition text-sm font-semibold">
                                Commander
                              </Link>
                            </>
                          ) : (
                            <Link to="/login" className="flex-1 bg-primary text-white text-center py-2 rounded-lg hover:bg-opacity-90 transition text-sm font-semibold">
                              Se connecter pour commander
                            </Link>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Utilisateur */}
            {user ? (
              <div 
                className="relative" 
                ref={userDropdownRef}
                onMouseEnter={handleUserMouseEnter}
                onMouseLeave={handleUserMouseLeave}
              >
                <button 
                  className="hover:text-secondary transition p-1"
                  onClick={toggleUserDropdown}
                >
                  <Icons.User />
                </button>
                {showUserDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white text-darkGray rounded-lg shadow-lg z-50 border border-gray-200">
                    <Link to="/account" className="block px-4 py-2 hover:bg-gray-100" onClick={() => setShowUserDropdown(false)}>
                      Mon compte
                    </Link>
                    <Link to="/account/orders" className="block px-4 py-2 hover:bg-gray-100" onClick={() => setShowUserDropdown(false)}>
                      Mes commandes
                    </Link>
                    {user.role === 'admin' && (
                      <Link to="/admin" className="block px-4 py-2 text-secondary hover:bg-gray-100" onClick={() => setShowUserDropdown(false)}>
                        Administration
                      </Link>
                    )}
                    <button 
                      onClick={() => {
                        setShowUserDropdown(false);
                        logout();
                      }} 
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-red-500"
                    >
                      Déconnexion
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="hover:text-secondary transition">
                <Icons.User />
              </Link>
            )}

            {/* Menu mobile */}
            <button className="lg:hidden" onClick={() => setIsOpen(!isOpen)}>
              <Icons.Menu />
            </button>
          </div>
        </div>

        {/* ✅ Menu mobile - arrière-plan blanc, texte adapté */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200 space-y-3 bg-white">
            <Link to="/" className="block py-2 text-primary hover:text-secondary transition font-medium">Accueil</Link>
            <Link to="/how-it-works" className="block py-2 text-primary hover:text-secondary transition font-medium">Livraison</Link>
            <Link to="/products" className="block py-2 text-primary hover:text-secondary transition font-medium">Boutique</Link>
            <Link to="/faq" className="block py-2 text-primary hover:text-secondary transition font-medium">FAQ</Link>
            <Link to="/contact" className="block py-2 text-primary hover:text-secondary transition font-medium">Contact</Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;